import { useMutation } from '@apollo/client';
import { Formik, Field, Form, ErrorMessage } from 'formik';
import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { INSERT_IMAGE_REVIEW } from '../../api/reviews';
import { useMediaQuery } from '../../layouts/Navbar/NavBar';
import { MyErrorObject } from './Header';
import { toast } from 'react-toastify';
import { Rating } from 'react-simple-star-rating';
import { useRouter } from 'next/router';
import MediaUploader, {
  ImageWithPreview as MediaImageWithPreview
} from '../../shared-components/MediaUploader/MediaUploader';
import Link from 'next/link';

import ImagesReviewModal from '../../shared-components/testimonials/imagesReviewModal';
interface IProps {
  rating: number;
  submitReview: Record<string, unknown>;
  setSubmitReview: React.Dispatch<
    React.SetStateAction<Record<string, unknown>>
  >;
  handleRating: (rate: number) => void;
  termsConditions: boolean;
  setTermsConditions: React.Dispatch<React.SetStateAction<boolean>>;
  errorStates: Record<string, boolean>;
  setErrorStates: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  errorObject: MyErrorObject;
  setErrorObject: React.Dispatch<React.SetStateAction<MyErrorObject>>;
  setDispModal: React.Dispatch<React.SetStateAction<boolean>>;
  setRefetch: React.Dispatch<React.SetStateAction<boolean>>;
  setReviewSubmitting: React.Dispatch<React.SetStateAction<boolean>>;
  images: MediaImageWithPreview[];
  setImages: React.Dispatch<React.SetStateAction<MediaImageWithPreview[]>>;
  onReviewSubmitted?: () => void;
}
interface RatingFieldProps {
  field: Record<string, any>;
  setFieldValue: (fieldName: string, value: any) => void;
}
interface Props {
  isChecked: boolean;
}
const CheckBoxTextStyle = styled.p<Props>`
  opacity: ${props => (props.isChecked ? '1' : '0.5')};
`;
const errorMsgObject: Record<string, any> = {
  date: 'Tour date must be selected',
  cities: 'Tour city must be selected',
  product: 'Tour must be selected'
};
const ReviewForm = ({
  rating,
  setSubmitReview,
  submitReview,
  handleRating,
  termsConditions,
  setTermsConditions,
  errorStates,
  setErrorStates,
  errorObject,
  setErrorObject,
  setDispModal,
  setRefetch,
  setReviewSubmitting,
  images,
  setImages,
  onReviewSubmitted
}: IProps) => {
  const [reviewInfo, setReviewInfo] = useState<Record<string, any>>({
    traveller: '',
    review: '',
    rating: 0,
    checkbox: false,
    bookingId: ''
  });
  const mediaQuery = useMediaQuery(768);
  const isNotDesktop = useMediaQuery(1439);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [allUploadsComplete, setAllUploadsComplete] = useState(true);
  const router = useRouter();
  const [
    insertReview,
    { data: mutationData, loading: mutationLoading, error }
  ] = useMutation(INSERT_IMAGE_REVIEW);

  const handleUploadStatusChange = (isUploading: boolean) => {
    setUploadingImages(isUploading);
  };

  useEffect(() => {
    const hasImages = images?.length > 0;
    const allUploaded = images.every(
      img => img?.uploadedUrl || img?.uploadError
    );
    const noneUploading = !images.some(img => img?.isUploading);

    setAllUploadsComplete(!hasImages || (allUploaded && noneUploading));
  }, [images]);
  const RatingField = ({ field, setFieldValue }: RatingFieldProps) => {
    const { name, value } = field;
    const handleRate = (rate: number) => {
      handleRating(rate);
      setFieldValue(name, rate);
    };
    return (
      <Rating
        onClick={handleRate}
        onPointerEnter={() => {}}
        onPointerLeave={() => {}}
        initialValue={value > 0 ? value : 0}
        fillColor="orange"
        emptyColor="gray"
        SVGstyle={{ display: 'inline-block' }}
        allowFraction
        size={mediaQuery ? 25 : 35}
        readonly={false}
        transition
      />
    );
  };

  const handleCheck = () => {
    // setTermsConditions(!termsConditions);
    // setReviewInfo({
    //   ...reviewInfo,
    //   checkbox: !termsConditions
    // });
    const errObj: Record<string, any> = {};
    Object.keys(errorMsgObject).forEach(key => {
      setTermsConditions(false);
      if (!submitReview[key]) {
        errObj[key] = errorMsgObject[key];
      }
    });
    setErrorObject(errObj);
    if (Object.keys(errObj).length === 0) {
      setTermsConditions(!termsConditions);
      setReviewInfo({
        ...reviewInfo,
        checkbox: !termsConditions
      });
    }
  };
  const handleSubmit = async (values: any) => {
    setReviewInfo(values);
    delete submitReview.checkbox;

    try {
      const uploadedImageUrls: string[] = images
        ?.filter(img => img?.uploadedUrl)
        ?.map(img => img?.uploadedUrl!);

      const reviewPayload: Record<string, any> = {
        ...submitReview,
        published_at: new Date(),
        source: 'WEBSITE'
      };

      if (uploadedImageUrls?.length > 0) {
        reviewPayload.images = uploadedImageUrls;
      }
      await insertReview({
        variables: {
          ...reviewPayload,
          rating: parseFloat(reviewPayload?.rating),
          product: parseInt(reviewPayload?.product, 10),
          city: parseInt(reviewPayload?.cities?.[0] || reviewPayload?.city, 10),
          date: new Date(reviewPayload?.date)?.toISOString(),
          published_at: new Date(reviewPayload?.published_at)?.toISOString(),
          created_at: new Date(),
          updated_at: new Date(),
          image_urls: reviewPayload?.images || null,
          booking_id: reviewInfo?.bookingId || null
        }
      });

      toast.success('Review submitted successfully');
      onReviewSubmitted?.();
      localStorage.removeItem('bookingId');
      // Clear bookingId from parent component state
      if (reviewInfo?.bookingId) {
        setReviewInfo(prev => ({
          ...prev,
          bookingId: ''
        }));
      }
      setUploadingImages(false);
      setDispModal(false);
      setRefetch(true);
    } catch (error: any) {
      toast.error(
        error.message || 'Something went wrong while submitting the review.'
      );
    }
  };
  useEffect(() => {
    const storedBookingId = localStorage.getItem('bookingId');
    if (storedBookingId) {
      setReviewInfo(prev => ({
        ...prev,
        bookingId: storedBookingId
      }));
    }
  }, []);
  const [showModal, setShowModal] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  return (
    <>
      <Formik
        initialValues={reviewInfo}
        validate={values => {
          setSubmitReview(prevState => {
            return {
              ...prevState,
              ...values
            };
          });
          const errors: any = {};
          if (
            !/^[a-zA-Z\s]*[a-zA-Z][a-zA-Z\s]*$/.test(values?.traveller) ||
            values?.traveller?.trim() === ''
          ) {
            errors.traveller = 'Enter Valid Name';
          } else if (values?.rating === 0) {
            errors.rating = 'Please Select Rating';
          } else if (values?.review?.length < 8) {
            errors.review = 'Enter Atleast 8 Character Review';
          } else if (!values?.checkbox) {
            errors.checkbox = 'Please agree to our Terms & Conditions';
          }
          return errors;
        }}
        onSubmit={async (values, { setSubmitting }) => {
          setReviewSubmitting(true);
          await handleSubmit(values);
          setSubmitting(false);
          setReviewSubmitting(false);
        }}
      >
        {({ isSubmitting, setFieldValue, values }) => (
          <Form>
            {reviewInfo?.bookingId && (
              <>
                <label className="block text-[16px] text-[#4F4F4F] font-[500] mt-3">
                  Booking ID
                </label>
                <Field
                  id="bookingId"
                  name="bookingId"
                  type="text"
                  value={reviewInfo?.bookingId}
                  placeholder="Booking ID"
                  disabled
                  className="focus:outline-none bg-[#F5F5F5] text-[16px] py-2 lg:py-3 px-3 w-[100%] rounded-[5px] lg:rounded-[10px] mt-1 text-gray-500 cursor-not-allowed"
                />
              </>
            )}
            <label className="block text-[16px] text-[#4F4F4F] font-[500] mt-3">
              Name
            </label>
            <Field
              id="traveller"
              name="traveller"
              type="text"
              placeholder="Name"
              autocomplete="off"
              required
              className="focus:outline-none bg-[#EEEEEE] text-[16px] py-2 lg:py-3 px-3 w-[100%] rounded-[5px] lg:rounded-[10px] mt-1"
            />
            <ErrorMessage
              name="traveller"
              component="div"
              className="text-red-400"
            />

            <div>
              <label className="block text-[16px] font-[500] text-[#4F4F4F] mt-3">
                Rating
              </label>
              <Field
                name="rating"
                component={RatingField}
                setFieldValue={setFieldValue}
              />
            </div>

            <ErrorMessage
              name="rating"
              component="div"
              className="text-red-400"
            />

            <Field
              id="review"
              name="review"
              as="textarea"
              placeholder="Write Your Review"
              className="block focus:outline-none w-[100%] rounded-[10px] py-2 lg:py-5 px-3 bg-[#EEEEEE] md:h-[120px] xl:h-[150px] min-h-[108px] mt-3 placeholder:text-[16px] lg:placeholder:text-[18px]"
            />
            <ErrorMessage
              name="review"
              component="div"
              className="text-red-400"
            />
            <label className="block text-[16px] text-[#4F4F4F] font-[500] mt-3">
              Add Media{' '}
              <span className="font-[300] text-[11px]">(Optional)</span>
            </label>
            <MediaUploader
              images={images}
              setImages={setImages}
              isNotDesktop={isNotDesktop}
              showTermsText={false}
              onUploadStatusChange={handleUploadStatusChange}
            />
            <div className="flex items-start gap-3 pt-3">
              <Field
                type="checkbox"
                name="termsConditions"
                checked={termsConditions}
                onChange={(e: any) => {
                  handleCheck();
                  setFieldValue('checkbox', e.target.checked);
                }}
                className="mt-2"
              />
              <CheckBoxTextStyle
                isChecked={termsConditions}
                className="text-[10px] text-justify lg:text-[14px] font-[500] text-[#4F4F4F] w-full"
              >
                I certify that this review is based on my experience and is my
                genuine opinion, and have not been offered any incentive or
                payment origniating from the establishment to rewrite this
                review. I understand that a See Sight Tours has a zero tolerance
                policy on fake reviews.
                {images?.length > 0 && (
                  <p className="mt-2">
                    By uploading this media, I confirm that I own the content,
                    give See Sight Tours permission to use the media, and agree
                    to the{' '}
                    <Link
                      href={{
                        pathname: '/terms-and-conditions',
                        query: { ref: router.asPath, fromImageUpload: 'true' }
                      }}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline text-blue-600 hover:text-blue-800 visited:text-purple-600"
                    >
                      terms & conditions
                    </Link>
                    .
                  </p>
                )}
              </CheckBoxTextStyle>
            </div>
            <ErrorMessage
              name="checkbox"
              component="div"
              className="text-red-400"
            />
            <button
              className={`py-2 px-10 mb-10 focus:outline-none text-[14px] sm:text-[16px] lg:text-[18px] font-[500] float-right rounded-[10px] mt-5 ${
                isSubmitting || mutationLoading || !allUploadsComplete
                  ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                  : 'bg-primary hover:bg-[#2E3D7A] active:bg-[#2E3D7A] text-white'
              }`}
              type="submit"
              disabled={isSubmitting || mutationLoading || !allUploadsComplete}
            >
              {mutationLoading
                ? 'Submitting...'
                : uploadingImages
                ? 'Uploading files...'
                : !allUploadsComplete
                ? 'Uploading files...'
                : 'Submit'}
            </button>
          </Form>
        )}
      </Formik>

      {showModal && (
        <ImagesReviewModal
          data={{
            isVideo: images?.map(img => img?.file?.type?.startsWith('video')),
            fileTypes: images?.map(img => img?.file?.type)
          }}
          setShowModal={setShowModal}
          productName="Preview Media"
          randomName="Preview"
          media={images?.map(img => img?.preview)}
          initialIndex={activeIndex}
        />
      )}
    </>
  );
};

export default ReviewForm;
